import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOutlinedIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import Dialog from "@mui/material/Dialog";
import MDBadge from "components/MDBadge";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { ToastContainer, toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import Select from "react-select";
import colors from "assets/theme/base/colors";
import "react-toastify/dist/ReactToastify.css";
import "../campus/Campus.css";
import { hasAdmissionPermission } from "hooks/permissions";

const { info } = colors;

const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

const AdmissionDraftStructures = () => {
  // State variables
  const [campaigns, setCampaigns] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [draftStructures, setDraftStructures] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [totalFee, setTotalFee] = useState("");
  const [showAddDraftModal, setShowAddDraftModal] = useState(false);
  const [showUpdateDraftModal, setShowUpdateDraftModal] = useState(false);
  const [showViewDraftModal, setShowViewDraftModal] = useState(false);
  const [showDeleteDraftModal, setShowDeleteDraftModal] = useState(false);
  const [currentDraftStructureId, setCurrentDraftStructureId] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedDraftStructure, setSelectedDraftStructure] = useState(null);
  const [formErrors, setFormErrors] = useState({
    campaign: "",
    program: "",
    totalFee: "",
  });
  const [filteredDraftStructures, setFilteredDraftStructures] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch campaigns
  const getCampaigns = async () => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      setLoading(true);
      const response = await fetch(
        `${BASE_URL}/campaign/get_campaigns`,
        requestOptions
      );
      console.log("Campaigns response status:", response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Campaigns data received:", data);
      setCampaigns(data.campaigns || []);
    } catch (error) {
      console.error("Fetch campaigns error:", error);
      toast.error("Failed to fetch campaigns");
    } finally {
      setLoading(false);
    }
  };

  // Fetch degrees in campaign
  const getDegreesInCampaign = async (campaignId) => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const response = await fetch(
        `${BASE_URL}/campaign/get_degrees_in_campaign/${campaignId}`,
        requestOptions
      );
      console.log("Degrees response status:", response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Degrees data received:", data);
      setDegrees(data.degrees || []);
      
      // Extract programs from degrees
      const allPrograms = [];
      data.degrees?.forEach(degree => {
        if (degree.degree_name) {
          allPrograms.push({
            id: degree.degree_id,
            program_name: degree.degree_name,
          });
        }
      });
      setPrograms(allPrograms);
    } catch (error) {
      console.error("Fetch degrees error:", error);
      toast.error("Failed to fetch degrees");
      setDegrees([]);
      setPrograms([]);
    }
  };

  // Fetch draft structures
  const getDraftStructures = async (campaignId) => {
    if (!campaignId) return;
    
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      setLoading(true);
      const response = await fetch(
        `${BASE_URL}/campaign/get_draft_structure/${campaignId}`,
        requestOptions
      );
      console.log("Draft structures response status:", response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Draft structures data received:", data);
      setDraftStructures(data.draft_structures || []);
      setFilteredDraftStructures(data.draft_structures || []);
    } catch (error) {
      console.error("Fetch draft structures error:", error);
      toast.error("Failed to fetch draft structures");
      setDraftStructures([]);
      setFilteredDraftStructures([]);
    } finally {
      setLoading(false);
    }
  };

  // Add draft structure
  const addDraftStructure = async () => {
    if (!validateDraftForm()) return;

    const draftData = {
      campaign_id: selectedCampaign.value,
      program_id: selectedProgram.value,
      total_fee: parseFloat(totalFee),
    };

    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/campaign/add_draft_structure`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(draftData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add draft structure");
      }

      const newDraftStructure = await response.json();
      toast.success("Draft structure added successfully");
      getDraftStructures(selectedCampaign.value);
      resetForm();
    } catch (error) {
      console.error("Error adding draft structure:", error);
      toast.error(error.message || "Failed to add draft structure");
    } finally {
      setShowAddDraftModal(false);
      setLoading(false);
    }
  };

  // Update draft structure
  const updateDraftStructure = async () => {
    if (!validateDraftForm()) return;

    const draftData = {
      id: currentDraftStructureId,
      campaign_id: selectedCampaign.value,
      program_id: selectedProgram.value,
      total_fee: parseFloat(totalFee),
    };

    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/campaign/update_draft_structure`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(draftData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update draft structure");
      }

      toast.success("Draft structure updated successfully");
      getDraftStructures(selectedCampaign.value);
      resetForm();
    } catch (error) {
      console.error("Error updating draft structure:", error);
      toast.error(error.message || "Failed to update draft structure");
    } finally {
      setShowUpdateDraftModal(false);
      setLoading(false);
    }
  };

  // Delete draft structure
  const deleteDraftStructure = async () => {
    try {
      setLoading(true);
      console.log("Deleting draft structure with ID:", currentDraftStructureId);
      const response = await fetch(
        `${BASE_URL}/campaign/delete_draft_structure/${currentDraftStructureId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Delete response status:", response.status);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete draft structure");
      }

      toast.success("Draft structure deleted successfully");
      getDraftStructures(selectedCampaign.value);
    } catch (error) {
      console.error("Error deleting draft structure:", error);
      toast.error(error.message || "Failed to delete draft structure");
    } finally {
      setShowDeleteDraftModal(false);
      setLoading(false);
    }
  };

  // View draft structure
  const viewDraftStructure = (id) => {
    const draftStructure = draftStructures.find((ds) => ds.id === id);
    setSelectedDraftStructure(draftStructure);
    setShowViewDraftModal(true);
  };

  // Search functionality
  const handleSearchKeywordChange = (e) => {
    const searchTerm = e.target.value.trim().toLowerCase();
    setSearchKeyword(searchTerm);

    if (searchTerm === "") {
      setFilteredDraftStructures(draftStructures);
    } else {
      const filtered = draftStructures.filter((draftStructure) => {
        const programMatch = draftStructure.program_name && 
          draftStructure.program_name.toLowerCase().includes(searchTerm);
        const idMatch = draftStructure.id && 
          draftStructure.id.toString().includes(searchTerm);
        const feeMatch = draftStructure.total_fee && 
          draftStructure.total_fee.toString().includes(searchTerm);
        const campaignMatch = draftStructure.campaign_id && 
          draftStructure.campaign_id.toString().includes(searchTerm);
        const programIdMatch = draftStructure.program_id && 
          draftStructure.program_id.toString().includes(searchTerm);
        return programMatch || idMatch || feeMatch || campaignMatch || programIdMatch;
      });
      setFilteredDraftStructures(filtered);
    }
  };

  // Form validation
  const validateDraftForm = () => {
    const errors = {};
    
    if (!selectedCampaign) {
      errors.campaign = " * Campaign is required";
    }
    if (!selectedProgram) {
      errors.program = " * Program is required";
    }
    if (!totalFee || totalFee.trim() === "") {
      errors.totalFee = " * Total fee is required";
    } else if (isNaN(totalFee) || parseFloat(totalFee) <= 0) {
      errors.totalFee = " * Total fee must be a positive number";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle focus to clear errors
  const handleFocus = (fieldName) => {
    setFormErrors((prevFormErrors) => ({
      ...prevFormErrors,
      [fieldName]: "",
    }));
  };

  // Reset form
  const resetForm = () => {
    setSelectedProgram(null);
    setTotalFee("");
    setFormErrors({
      campaign: "",
      program: "",
      totalFee: "",
    });
  };

  // Handle campaign selection
  const handleCampaignChange = (selectedOption) => {
    setSelectedCampaign(selectedOption);
    if (selectedOption) {
      getDegreesInCampaign(selectedOption.value);
      getDraftStructures(selectedOption.value);
    } else {
      setDegrees([]);
      setPrograms([]);
      setDraftStructures([]);
      setFilteredDraftStructures([]);
    }
    setSelectedProgram(null);
    handleFocus('campaign');
  };

  // Dialog handlers
  const showDraftAddModal = () => {
    resetForm();
    setShowAddDraftModal(true);
  };

  const showDraftUpdateModal = (id) => {
    const draftStructure = draftStructures.find((ds) => ds.id === id);
    if (draftStructure) {
      setCurrentDraftStructureId(id);
      setSelectedProgram({
        value: draftStructure.program_id,
        label: draftStructure.program_name
      });
      setTotalFee(draftStructure.total_fee.toString());
      setShowUpdateDraftModal(true);
    }
  };

  const showDraftDeleteModal = (id) => {
    setCurrentDraftStructureId(id);
    setShowDeleteDraftModal(true);
  };

  // Initial render
  useEffect(() => {
    getCampaigns();
  }, []);

  // Prepare options for react-select
  const campaignOptions = campaigns.map((campaign) => ({
    value: campaign.id,
    label: campaign.name,
  }));

  const programOptions = programs.map((program) => ({
    value: program.id,
    label: `${program.program_name}`,
  }));

  // Table columns
  const columns = [
    { Header: "ID", accessor: "id", align: "center" },
    { Header: "Campaign ID", accessor: "campaign_id", align: "center" },
    { Header: "Program ID", accessor: "program_id", align: "center" },
    { Header: "Program Name", accessor: "program_name", align: "center" },
    { Header: "Total Fee", accessor: "total_fee", align: "center" },
    { Header: "Actions", accessor: "actions", align: "center" },
  ];

  // Table rows
  const rows = filteredDraftStructures.map((draftStructure) => ({
    id: draftStructure.id || "N/A",
    campaign_id: draftStructure.campaign_id || "N/A",
    program_id: draftStructure.program_id || "N/A",
    program_name: draftStructure.program_name || "N/A",
    total_fee: `PKR ${draftStructure.total_fee?.toLocaleString() || "0"}`,
    actions: (
      <>
        {hasAdmissionPermission("DraftStructure", "UPDATE") && (
          <IconButton onClick={() => showDraftUpdateModal(draftStructure.id)}>
            <EditIcon className="icon-button-edit" />
          </IconButton>
        )}
        {hasAdmissionPermission("DraftStructure", "READ") && (
          <IconButton onClick={() => viewDraftStructure(draftStructure.id)}>
            <VisibilityOutlinedIcon className="icon-button-view" />
          </IconButton>
        )}
        {hasAdmissionPermission("DraftStructure", "DELETE") && (
          <IconButton onClick={() => showDraftDeleteModal(draftStructure.id)}>
            <DeleteIcon className="icon-button-delete" />
          </IconButton>
        )}
      </>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card className="mt-5 card1">
              <MDBox
                mx={0}
                mt={0}
                py={2}
                px={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                  <div style={{ minWidth: "300px" }}>
                    <Select
                      options={campaignOptions}
                      value={selectedCampaign}
                      onChange={handleCampaignChange}
                      placeholder="Select Campaign"
                      isClearable
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: "40px",
                          fontSize: "14px",
                        }),
                      }}
                    />
                  </div>
                  <Paper component="form" className="search-paper">
                    <InputBase
                      sx={{ ml: 1, flex: 1, fontSize: "16px" }}
                      placeholder="Search"
                      inputProps={{ "aria-label": "search draft structure" }}
                      value={searchKeyword}
                      onChange={handleSearchKeywordChange}
                    />
                    <SearchIcon className="search-icon" />
                  </Paper>
                </div>
                {hasAdmissionPermission("DraftStructure", "CREATE") && (
                  <Button
                    variant="filled"
                    endIcon={<AddIcon className="add-icon" />}
                    onClick={showDraftAddModal}
                    className="add-button"
                    disabled={loading}
                  >
                    Add
                  </Button>
                )}
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Add Draft Structure Dialog */}
      <Dialog
        open={showAddDraftModal}
        onClose={() => setShowAddDraftModal(false)}
        aria-labelledby="responsive-dialog-title"
        maxWidth="lg"
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-title">
          {"Add Draft Structure"}
        </DialogTitle>
        <DialogContent className="dialog-content">
          <div className="row">
            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Campaign</label>
                <Select
                  options={campaignOptions}
                  value={selectedCampaign}
                  onChange={handleCampaignChange}
                  placeholder="Select Campaign"
                  isClearable
                  onFocus={() => handleFocus('campaign')}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "48px",
                      fontSize: "16px",
                    }),
                  }}
                />
              </div>
              <div className="error-message1">{formErrors.campaign}</div>
            </div>

            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Program</label>
                <Select
                  options={programOptions}
                  value={selectedProgram}
                  onChange={(selectedOption) => {
                    setSelectedProgram(selectedOption);
                    handleFocus('program');
                  }}
                  placeholder="Select Program"
                  isClearable
                  onFocus={() => handleFocus('program')}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "48px",
                      fontSize: "16px",
                    }),
                  }}
                />
              </div>
              <div className="error-message1">{formErrors.program}</div>
            </div>

            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Total Fee</label>
                <input
                  name="total_fee"
                  type="number"
                  step="0.01"
                  min="0"
                  value={totalFee}
                  onChange={(e) => setTotalFee(e.target.value)}
                  onFocus={() => handleFocus('totalFee')}
                  className="form-control form-control-lg"
                  placeholder="Enter total fee"
                />
              </div>
              <div className="error-message1">{formErrors.totalFee}</div>
            </div>
          </div>
        </DialogContent>
        <DialogActions style={{ marginRight: "20px" }}>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowAddDraftModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            className="dialog-actions-button positive" 
            onClick={addDraftStructure}
            disabled={loading}
          >
            {loading ? "Adding..." : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Draft Structure Dialog */}
      <Dialog
        open={showUpdateDraftModal}
        onClose={() => setShowUpdateDraftModal(false)}
        aria-labelledby="responsive-dialog-title"
        maxWidth="lg"
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-title">
          {"Update Draft Structure"}
        </DialogTitle>
        <DialogContent className="dialog-content">
          <div className="row">
            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Campaign</label>
                <Select
                  options={campaignOptions}
                  value={selectedCampaign}
                  onChange={handleCampaignChange}
                  placeholder="Select Campaign"
                  isClearable
                  onFocus={() => handleFocus('campaign')}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "48px",
                      fontSize: "16px",
                    }),
                  }}
                />
              </div>
              <div className="error-message1">{formErrors.campaign}</div>
            </div>

            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Program</label>
                <Select
                  options={programOptions}
                  value={selectedProgram}
                  onChange={(selectedOption) => {
                    setSelectedProgram(selectedOption);
                    handleFocus('program');
                  }}
                  placeholder="Select Program"
                  isClearable
                  onFocus={() => handleFocus('program')}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "48px",
                      fontSize: "16px",
                    }),
                  }}
                />
              </div>
              <div className="error-message1">{formErrors.program}</div>
            </div>

            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Total Fee</label>
                <input
                  name="total_fee"
                  type="number"
                  step="0.01"
                  min="0"
                  value={totalFee}
                  onChange={(e) => setTotalFee(e.target.value)}
                  onFocus={() => handleFocus('totalFee')}
                  className="form-control form-control-lg"
                  placeholder="Enter total fee"
                />
              </div>
              <div className="error-message1">{formErrors.totalFee}</div>
            </div>
          </div>
        </DialogContent>
        <DialogActions style={{ marginRight: "20px" }}>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowUpdateDraftModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={updateDraftStructure}
            disabled={loading}
          >
            {loading ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Draft Structure Dialog */}
      <Dialog
        open={showDeleteDraftModal}
        onClose={() => setShowDeleteDraftModal(false)}
        PaperProps={{
          sx: {
            width: "450px",
            borderRadius: "20px",
            opacity: 1,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-delete">
          Delete Draft Structure
        </DialogTitle>
        <DialogContent className="content-delete">
          Are you sure you want to delete this draft structure?
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowDeleteDraftModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={deleteDraftStructure}
            disabled={loading}
          >
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Draft Structure Dialog */}
      <Dialog
        open={showViewDraftModal}
        onClose={() => setShowViewDraftModal(false)}
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-delete">
          View Draft Structure
        </DialogTitle>
        <DialogContent className="dialog-content">
          {selectedDraftStructure && (
            <>
              <div className="flex-container">
                <strong className="label1">ID</strong>
                <span className="value1">{selectedDraftStructure.id || "N/A"}</span>
              </div>
              <div className="flex-container">
                <strong className="label1">Campaign ID</strong>
                <span className="value1">{selectedDraftStructure.campaign_id || "N/A"}</span>
              </div>
              <div className="flex-container">
                <strong className="label1">Program ID</strong>
                <span className="value1">{selectedDraftStructure.program_id || "N/A"}</span>
              </div>
              <div className="flex-container">
                <strong className="label1">Program Name</strong>
                <span className="value1">{selectedDraftStructure.program_name || "N/A"}</span>
              </div>
              <div className="flex-container">
                <strong className="label1">Total Fee</strong>
                <span className="value1">PKR {selectedDraftStructure.total_fee?.toLocaleString() || "0"}</span>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowViewDraftModal(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdmissionDraftStructures;